import { Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, ListItemText, MenuItem, Select, Typography } from '@mui/material';
import greyDialogTitleSx from '../Dialog/greyDialogTitleSx';
import CloseIcon from '@mui/icons-material/Close';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import generatePDF, { Resolution, Margin, Options } from 'react-to-pdf';
import { toast } from 'react-toastify';
import moment from 'moment';
import { FC, ReactNode, useEffect, useState } from 'react';
import axios from '../../utils/axios';
import { createExportProgress } from '../Common/ExportProgressToast';
import { Utils } from '../../utils/common-utils';

// The PDF is a screenshot of a DOM table, so it gets a lower ceiling than Excel.
const PDF_MAX_ROWS = 1000;
/** Preview page size; the dialog fetches these on open and more as you scroll. */
export const PREVIEW_PAGE_SIZE = 50;

// id → name for all the account's tags (inactive too, old records use them); never throws.
export const fetchTagNames = async (appliesTo: string): Promise<Record<string, string>> => {
  try {
    const response = await axios.get(`/api/v1/tag?appliesTo=${appliesTo}`);
    const names: Record<string, string> = {};
    (response.data || []).forEach((tag: any) => { if (tag?._id) names[tag._id] = tag.name; });
    return names;
  } catch (error) {
    console.error('Error fetching tags for report:', error);
    return {};
  }
};

/** Tags come back populated or as bare ids depending on the endpoint. */
export const tagNamesOf = (tags: any, tagNames: Record<string, string>): string[] =>
  (Array.isArray(tags) ? tags : [])
    .map((tag: any) => tag?.name || tagNames[tag?._id || tag])
    .filter(Boolean);

/** Writes the picked columns to an .xlsx and saves it. */
export const downloadRowsAsExcel = (rows: any[], columns: string[], fileName: string, sheetName: string) => {
  const picked = rows.map((row) => {
    const out: any = {};
    columns.forEach((c) => { out[c] = row[c]; });
    return out;
  });
  const worksheet = XLSX.utils.json_to_sheet(picked);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${fileName}.xlsx`
  );
};

/** Location may be populated or a bare id. */
export const locationNameOf = (location: any, locations: any[]): string =>
  location?.name || (locations || []).find((l: any) => l._id === (location?._id || location))?.name || '';

/** Builds the PDF table straight on document.body, so the download survives leaving the page. */
const buildPdfNode = (title: string, subtitle: string, rows: any[]) => {
  const el = (tag: string, style: string, text?: string) => {
    const node = document.createElement(tag);
    node.setAttribute('style', style);
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const headers = Object.keys(rows[0]);
  const wrap = el('div', 'position:fixed;left:-10000px;top:0;width:1400px;background:#fff');
  const page = el('div', 'padding:16px;font-family:Arial, sans-serif');
  page.appendChild(el('h2', 'margin:0 0 4px 0;font-size:18px', title));
  page.appendChild(el('p', 'margin:0 0 12px 0;font-size:11px;color:#666', subtitle));
  const table = el('table', 'width:100%;border-collapse:collapse;font-size:10px');
  const headRow = el('tr', '');
  headers.forEach((h) => headRow.appendChild(el('th', 'border:1px solid #dcdfe5;background:#488dff;color:#fff;padding:5px;text-align:left', h)));
  table.appendChild(el('thead', '')).appendChild(headRow);
  const body = table.appendChild(el('tbody', ''));
  rows.forEach((row, i) => {
    const tr = body.appendChild(el('tr', `background:${i % 2 ? '#f7f9fc' : '#fff'}`));
    headers.forEach((h) => tr.appendChild(el('td', 'border:1px solid #dcdfe5;padding:5px', formatCell(row[h], h))));
  });
  page.appendChild(table);
  wrap.appendChild(page);
  document.body.appendChild(wrap);
  return { page, remove: () => wrap.remove() };
};

interface ReportExportDialogProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Sales Report" — used for the dialog heading and the PDF heading. */
  title: string;
  loading: boolean;
  /** Flattened rows; keys are the column names. */
  rows: any[];
  /** Every column the report can carry, in display order. */
  columns: string[];
  /** Columns ticked when the dialog opens; all of them if omitted. */
  defaultColumns?: string[];
  /** Download file name, without extension. */
  fileName: () => string;
  sheetName: string;
  /** Extra line under the PDF heading (e.g. the date range). */
  pdfSubtitle?: string;
  /** Total matching the filters; `rows` is just the slice fetched so far. */
  totalCount?: number;
  /** Fetches the next preview page when the table is scrolled near its end. */
  onLoadMore?: () => void;
  loadingMore?: boolean;
  /** Pulls every matching record. Called only on download, so the preview stays cheap. */
  fetchAllRows?: (onProgress?: (loaded: number, total: number) => void) => Promise<any[]>;
  /** Short note shown under the record count. */
  note?: string;
  /** Extra filter controls shown next to the column picker. */
  filters?: ReactNode;
}

// ID / number columns (e.g. "Trxn ID", "Invoice No") are identifiers, not money
const isIdColumn = (header: string) => /\b(id|no|number)\b|#/i.test(header.trim());

// Plain counts (e.g. "Age") — not money, no ₹
const isPlainNumberColumn = (header: string) => /^age$/i.test(header.trim());

const formatCell = (value: any, header = '') => {
  if (typeof value !== 'number') return String(value ?? '-');
  if (isPlainNumberColumn(header)) return String(value);
  return isIdColumn(header) ? `#${value}` : Utils.formatNumber(value, true, true);
};

// Report preview for Sales / Purchase / Payments: pick columns, then download Excel or PDF.
const ReportExportDialog: FC<ReportExportDialogProps> = ({ open, onClose, title, loading, rows, columns, defaultColumns, fileName, sheetName, pdfSubtitle, totalCount, onLoadMore, loadingMore, fetchAllRows, note, filters }) => {
  const initialCols = defaultColumns ? columns.filter((c) => defaultColumns.includes(c)) : columns;
  const [selectedCols, setSelectedCols] = useState<string[]>(initialCols);
  const [isExporting, setIsExporting] = useState(false);

  // Every fresh open starts with the default columns picked.
  useEffect(() => {
    if (open) setSelectedCols(initialCols);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Column dropdown: the "All" entry toggles every column; picks always keep the original column order.
  const ALL_OPTION = '__all__';
  const handleColsChange = (value: string[]) => {
    if (value.includes(ALL_OPTION)) {
      setSelectedCols(selectedCols.length === columns.length ? [] : columns);
      return;
    }
    setSelectedCols(columns.filter((c) => value.includes(c)));
  };

  /** Keep only the picked columns, in order; array cells (e.g. Items) spread into "Item 1..N", N = longest in the data. */
  const pickRows = (data: any[]) => {
    const cols = columns.filter((c) => selectedCols.includes(c));
    const spread: Record<string, number> = Object.fromEntries(cols.filter((c) => data.some((r) => Array.isArray(r[c])))
      .map((c) => [c, Math.max(1, ...data.map((r) => r[c]?.length || 0))]));
    return data.map((row) => {
      const out: any = {};
      cols.forEach((c) => {
        if (!spread[c]) out[c] = row[c];
        else for (let i = 0; i < spread[c]; i++) out[`${c.replace(/s$/, '')} ${i + 1}`] = row[c]?.[i] ?? '-';
      });
      return out;
    });
  };
  const previewRows = pickRows(rows);
  const previewHeaders = Object.keys(previewRows[0] || {});

  const handleExport = async (format: 'excel' | 'pdf') => {
    if (rows.length === 0) {
      toast.info('Nothing to download for the selected filters.');
      return;
    }
    if (selectedCols.length === 0) {
      toast.info('Select at least one column.');
      return;
    }
    setIsExporting(true);
    // Runs in the background; the bottom-right toast tracks it while the preview stays usable.
    const progress = createExportProgress(title, format === 'excel' ? 'Excel' : 'PDF');
    try {
      // Only now pull every record; the preview above is a page at a time.
      const allRows = fetchAllRows ? await fetchAllRows(progress.update) : rows;
      const picked = pickRows(allRows);

      if (format === 'excel') {
        downloadRowsAsExcel(picked, Object.keys(picked[0] || {}), fileName(), sheetName);
      } else {
        progress.building();
        const pdfOptions: Options = {
          filename: `${fileName()}.pdf`,
          method: 'save',
          resolution: Resolution.NORMAL,
          page: { margin: Margin.SMALL, format: 'A4', orientation: 'landscape' },
          canvas: { mimeType: 'image/jpeg', qualityRatio: 1 },
          overrides: { pdf: { compress: true }, canvas: { useCORS: true } }
        };
        // react-to-pdf paints a real DOM node, so build the rows off-screen first.
        const pdfRows = picked.slice(0, PDF_MAX_ROWS);
        const pdf = buildPdfNode(title, `${pdfRows.length} record(s)${pdfSubtitle ? ` · ${pdfSubtitle}` : ''} · generated ${moment().format('DD MMM YYYY, hh:mm A')}`, pdfRows);
        try {
          await generatePDF(() => pdf.page, pdfOptions);
        } finally {
          pdf.remove();
        }

        if (picked.length > PDF_MAX_ROWS) {
          toast.warning(`The PDF holds the first ${PDF_MAX_ROWS} records. Use Excel for the full list.`);
        }
      }
      progress.finish(format === 'pdf' ? Math.min(picked.length, PDF_MAX_ROWS) : picked.length);
    } catch (error) {
      console.error('Report export failed:', error);
      progress.fail('Could not download the report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const downloadDisabled = isExporting || loading || rows.length === 0 || selectedCols.length === 0;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={greyDialogTitleSx}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5">{title} Preview</Typography>
              {/* <Typography variant="body2">
                {loading ? 'Loading records…' : `${totalCount ?? rows.length} record(s) match the filters above`}
              </Typography> */}
              {note && <Typography variant="caption" color="text.secondary">Note: {note}</Typography>}
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>Columns</Typography>
            <Select
              multiple
              size="small"
              displayEmpty
              value={selectedCols}
              onChange={(e) => handleColsChange(e.target.value as string[])}
              renderValue={(sel) => {
                const count = (sel as string[]).length;
                if (count === columns.length) return 'All columns';
                if (count === 0) return 'No columns';
                return `${count} of ${columns.length} columns`;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 360,
                    // No green "selected" fill — the ticked checkbox already shows the pick.
                    '& .MuiMenuItem-root.Mui-selected': { backgroundColor: 'transparent' },
                    '& .MuiMenuItem-root.Mui-selected:hover, & .MuiMenuItem-root:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  },
                },
              }}
              sx={{ minWidth: 220, fontSize: 13, borderRadius: '8px', backgroundColor: '#fff' }}
            >
              <MenuItem dense value={ALL_OPTION} sx={{ borderBottom: '1px solid #e5e7eb' }}>
                <Checkbox
                  size="small"
                  checked={selectedCols.length === columns.length}
                  indeterminate={selectedCols.length > 0 && selectedCols.length < columns.length}
                />
                <ListItemText primary="All" primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
              </MenuItem>
              {columns.map((col) => (
                <MenuItem key={col} dense value={col}>
                  <Checkbox size="small" checked={selectedCols.includes(col)} />
                  <ListItemText primary={col} primaryTypographyProps={{ fontSize: 13 }} />
                </MenuItem>
              ))}
            </Select>
            {filters}

            {/* Downloads sit on the columns row, at its right end. */}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => handleExport('excel')}
                disabled={downloadDisabled}
                startIcon={<GridOnOutlinedIcon />}
                sx={{ textTransform: 'none', height: 36, borderRadius: '8px' }}
              >
                Download Excel
              </Button>
              <Button
                variant="contained"
                disableElevation
                onClick={() => handleExport('pdf')}
                disabled={downloadDisabled}
                startIcon={isExporting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PictureAsPdfOutlinedIcon />}
                sx={{ textTransform: 'none', height: 36, borderRadius: '8px' }}
              >
                {isExporting ? 'Preparing…' : 'Download PDF'}
              </Button>
            </Box>
          </Box>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress color="info" />
              <Typography sx={{ mt: 1.5, color: '#6b7280' }}>Fetching records for the selected filters…</Typography>
            </Box>
          ) : rows.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>Nothing to download for the selected filters.</Typography>
          ) : selectedCols.length === 0 ? (
            <Typography sx={{ textAlign: 'center', py: 6, color: '#6b7280' }}>Select at least one column to preview.</Typography>
          ) : (
            <>
            <Box
              onScroll={(e) => {
                const el = e.currentTarget;
                const nearEnd = el.scrollHeight - el.scrollTop - el.clientHeight < 200;
                if (nearEnd && !loadingMore && rows.length < (totalCount ?? rows.length)) onLoadMore?.();
              }}
              sx={{ overflow: 'auto', maxHeight: '60vh', border: '1px solid #dcdcdc', borderRadius: '8px' }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {previewHeaders.map((header) => (
                      <th key={header} style={{ position: 'sticky', top: 0, borderBottom: '1px solid #dcdcdc', borderRight: '1px solid #dcdcdc', backgroundColor: '#e9ecf1', color: '#1a1a1a', fontWeight: 600, padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row: any, index: number) => (
                    <tr key={index} style={{ backgroundColor: index % 2 ? '#f5f6f8' : '#fff' }}>
                      {previewHeaders.map((header) => (
                        <td key={header} style={{ borderBottom: '1px solid #ececec', borderRight: '1px solid #ececec', padding: '7px 10px', color: '#1a1a1a', whiteSpace: 'nowrap' }}>
                          {formatCell(row[header], header)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
            {loadingMore && (
              <Box sx={{ textAlign: 'center', pt: 1 }}><CircularProgress color="info" size={20} /></Box>
            )}
            {totalCount !== undefined && totalCount > rows.length && (
              <Typography sx={{ fontSize: '13px', color: '#6b7280', pt: 1, textAlign: 'center' }}>
                Showing {rows.length} of {totalCount} records — scroll for more. The download includes all of them.
              </Typography>
            )}
            </>
          )}
        </DialogContent>

      </Dialog>

    </>
  );
};

export default ReportExportDialog;
